import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  generateChartData, KPI_DATA, TRAFFIC_SOURCES,
  TOP_PAGES, DEVICES, COUNTRIES, RECENT_ACTIVITY
} from './analyticsData';
import MiniChart, { BarChart, DonutChart } from './MiniChart';
import './Analytics.css';

function Analytics() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(30);
  const [activeMetric, setActiveMetric] = useState('visitors');

  const chartData = useMemo(() => generateChartData(dateRange), [dateRange]);

  const metricColors = {
    visitors: '#7c83ff',
    pageViews: '#e040fb',
    sessions: '#00d2d3',
    revenue: '#00c853',
    bounceRate: '#ff6b35',
  };

  const activityIcons = { purchase: '💳', signup: '👤', review: '⭐', alert: '⚠️' };

  return (
    <div className="analytics-app">
      <div className="analytics-topbar">
        <div className="analytics-topbar-left">
          <button className="analytics-back-btn" onClick={() => navigate('/')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="analytics-title">Analytics Dashboard</h1>
        </div>
        <div className="analytics-topbar-right">
          <div className="analytics-date-range">
            {[7, 14, 30, 90].map((d) => (
              <button
                key={d}
                className={`analytics-range-btn ${dateRange === d ? 'active' : ''}`}
                onClick={() => setDateRange(d)}
              >
                {d}D
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="analytics-content">
        {/* KPI Cards */}
        <div className="analytics-kpis">
          {Object.entries(KPI_DATA).map(([key, kpi]) => (
            <div key={key} className="analytics-kpi">
              <div className="analytics-kpi-header">
                <span className="analytics-kpi-icon">{kpi.icon}</span>
                <span className={`analytics-kpi-change ${kpi.positive ? 'positive' : 'negative'}`}>
                  {kpi.change}
                </span>
              </div>
              <div className="analytics-kpi-value">{kpi.value}</div>
              <div className="analytics-kpi-label">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
            </div>
          ))}
        </div>

        {/* Main Chart */}
        <div className="analytics-card analytics-main-chart">
          <div className="analytics-card-header">
            <h2>Traffic Overview</h2>
            <div className="analytics-metric-tabs">
              {Object.entries(metricColors).map(([key, color]) => (
                <button
                  key={key}
                  className={`analytics-metric-tab ${activeMetric === key ? 'active' : ''}`}
                  style={activeMetric === key ? { background: color + '22', color, borderColor: color } : {}}
                  onClick={() => setActiveMetric(key)}
                >
                  <span className="analytics-metric-dot" style={{ background: color }}></span>
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </button>
              ))}
            </div>
          </div>
          <MiniChart
            data={chartData}
            dataKey={activeMetric}
            color={metricColors[activeMetric]}
            height={120}
            showLabels
            showGrid
          />
        </div>

        <div className="analytics-grid-2">
          {/* Revenue Bar Chart */}
          <div className="analytics-card">
            <div className="analytics-card-header">
              <h2>Revenue</h2>
              <span className="analytics-card-badge">Last {dateRange} days</span>
            </div>
            <BarChart data={chartData} dataKey="revenue" color="var(--accent)" height={160} />
          </div>

          {/* Traffic Sources */}
          <div className="analytics-card">
            <div className="analytics-card-header">
              <h2>Traffic Sources</h2>
            </div>
            <div className="analytics-sources">
              <DonutChart segments={TRAFFIC_SOURCES} size={140} />
              <div className="analytics-sources-legend">
                {TRAFFIC_SOURCES.map((src) => (
                  <div key={src.name} className="analytics-source-item">
                    <span className="analytics-source-dot" style={{ background: src.color }}></span>
                    <span className="analytics-source-name">{src.name}</span>
                    <span className="analytics-source-value">{src.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="analytics-grid-2">
          {/* Top Pages */}
          <div className="analytics-card">
            <div className="analytics-card-header">
              <h2>Top Pages</h2>
            </div>
            <div className="analytics-table-wrap">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Views</th>
                    <th>Unique</th>
                    <th>Avg. Time</th>
                    <th>Bounce</th>
                  </tr>
                </thead>
                <tbody>
                  {TOP_PAGES.map((page) => (
                    <tr key={page.path}>
                      <td className="analytics-page-path">{page.path}</td>
                      <td>{page.views.toLocaleString()}</td>
                      <td>{page.unique.toLocaleString()}</td>
                      <td>{page.avgTime}</td>
                      <td>{page.bounce}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Column */}
          <div className="analytics-right-col">
            {/* Devices */}
            <div className="analytics-card">
              <div className="analytics-card-header">
                <h2>Devices</h2>
              </div>
              <div className="analytics-devices">
                {DEVICES.map((device) => (
                  <div key={device.name} className="analytics-device">
                    <div className="analytics-device-header">
                      <span>{device.icon} {device.name}</span>
                      <span className="analytics-device-pct">{device.value}%</span>
                    </div>
                    <div className="analytics-device-bar">
                      <div
                        className="analytics-device-fill"
                        style={{ width: `${device.value}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Countries */}
            <div className="analytics-card">
              <div className="analytics-card-header">
                <h2>Top Countries</h2>
              </div>
              <div className="analytics-countries">
                {COUNTRIES.slice(0, 5).map((country) => (
                  <div key={country.name} className="analytics-country">
                    <span className="analytics-country-flag">{country.code}</span>
                    <span className="analytics-country-name">{country.name}</span>
                    <span className="analytics-country-visitors">{country.visitors.toLocaleString()}</span>
                    <div className="analytics-country-bar">
                      <div className="analytics-country-fill" style={{ width: `${country.pct}%` }}></div>
                    </div>
                    <span className="analytics-country-pct">{country.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h2>Recent Activity</h2>
            <span className="analytics-live-dot"></span>
          </div>
          <div className="analytics-activity">
            {RECENT_ACTIVITY.map((item, i) => (
              <div key={i} className="analytics-activity-item">
                <span className="analytics-activity-icon">{activityIcons[item.type]}</span>
                <span className="analytics-activity-text">{item.text}</span>
                <span className="analytics-activity-time">{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Analytics;
